// SINGLE SOURCE OF TRUTH — FRONTEND MIRROR OF BACKEND PERMISSION MODULE KEYS
//
// These MUST match the `MODULES` list defined in /app/backend/permission_models.py
// exactly. The CI script /app/scripts/check_permission_keys.py verifies this on
// every build; any drift will be reported.
//
// Historical bug (Apr 2026): the frontend used the string 'assessments' while
// the backend used 'fitness_assessment'. Non-admin Staff users lost access to
// the Fitness Assessments sidebar link because the key lookup returned undefined.
// Centralising the keys here — and gating via a CI check — prevents recurrence.
//
// USAGE:
//   import { PERMISSION_MODULES as PM } from '../constants/permissionModules';
//   hasModuleAccess(PM.FITNESS_ASSESSMENT, 'view')

export const PERMISSION_MODULES = Object.freeze({
  // 1. TODAY
  DASHBOARD: 'dashboard',
  FOCUS_HUB: 'focus_hub',
  // 2. ATHLETES & TEAMS
  ATHLETES: 'athletes',
  ATHLETE_TAGS: 'athlete_tags',
  TEAM_STATE: 'team_state',
  MEDIA_HIGHLIGHTS: 'media_highlights',
  // 3. PERFORMANCE
  TRAINING: 'training',
  ATTENDANCE: 'attendance',
  ATTENDANCE_FINALIZE: 'attendance.finalize',
  ATTENDANCE_REOPEN: 'attendance.reopen',
  ATTENDANCE_RESOLVE_ORPHAN: 'attendance.resolve_orphan',
  TRAINING_PROGRAMS: 'training_programs',
  MATCHES: 'matches',
  // Phase 89A — higher-risk match evidence-management action (dotted key, not
  // enforced by check_permission_keys.py; listed for discoverability). Default
  // OFF for everyone except Admin.
  MATCHES_MANAGE_EVIDENCE: 'matches.manage_evidence',
  MATCH_REPORTS: 'match_reports',
  FITNESS_ASSESSMENT: 'fitness_assessment',
  PDP: 'pdp',
  ALERTS_FLAGS: 'alerts_flags',
  ALERT_RULES: 'alert_rules',
  // 4. INSIGHTS & ANALYTICS
  DECISION_ENGINE: 'decision_engine',
  SQUAD_ANALYTICS: 'squad_analytics',
  ACADEMY_OVERVIEW: 'academy_overview',
  SEASON_REPORTS: 'season_reports',
  // Phase 87 — Trial Reports (now a first-class Access Control module).
  // TRIAL_REPORTS_EXPORT is an independently-controllable action permission
  // (Generate / Export / Print / Download). Dotted keys are intentionally not
  // enforced by check_permission_keys.py (which only compares non-dotted keys)
  // but are listed here for developer discoverability and IDE autocompletion.
  TRIAL_REPORTS: 'trial_reports',
  TRIAL_REPORTS_EXPORT: 'trial_reports.export',
  COMPARE_SEASONS: 'compare_seasons',
  COMPARE_ATHLETES: 'compare_athletes',
  // Phase 91A — Benchmark Library (the dotted `benchmarks.approve` action key is
  // Admin-managed and intentionally omitted here, matching attendance.finalize).
  BENCHMARKS: 'benchmarks',
  DEVELOPMENT_REPORTS: 'development_reports',
  // Phase 92 — Player Development Report dotted action permissions (independent
  // grants; default OFF except Full Staff/Admin). Not enforced by
  // check_permission_keys.py (dotted keys are skipped) but listed here for
  // discoverability and IDE autocompletion, mirroring TRIAL_REPORTS_EXPORT.
  DEVELOPMENT_REPORTS_FINALIZE: 'development_reports.finalize',
  DEVELOPMENT_REPORTS_EXPORT: 'development_reports.export',
  // Phase 92E.1 — lifecycle actions (duplicate/archive/restore/delete-draft).
  DEVELOPMENT_REPORTS_DUPLICATE: 'development_reports.duplicate',
  DEVELOPMENT_REPORTS_ARCHIVE: 'development_reports.archive',
  DEVELOPMENT_REPORTS_RESTORE: 'development_reports.restore',
  DEVELOPMENT_REPORTS_DELETE_DRAFT: 'development_reports.delete_draft',
  DEVELOPMENT_REPORTS_SHARE: 'development_reports.share',
  // 5. PLAYER HEALTH
  DAILY_MONITORING: 'daily_monitoring',
  INJURY_RECOVERY: 'injury_recovery',
  BIOMETRICS: 'biometrics',
  WELLNESS: 'wellness',
  MENTAL_HUB: 'mental_hub',
  // Phase 84.2 — Mental Hub subsections (Athlete Space / Parent Insights /
  // Mentorship). Independent view/create/edit/delete columns in the Access
  // Control UI. Backward-compat: staff with `mental_hub` at view/edit/delete
  // inherit the same level on all three subsections until an Admin sets an
  // explicit override.
  MENTAL_HUB_ATHLETE_SPACE:   'mental_hub.athlete_space',
  MENTAL_HUB_PARENT_INSIGHTS: 'mental_hub.parent_insights',
  MENTAL_HUB_MENTORSHIP:      'mental_hub.mentorship',
  // 6. DEVELOPMENT
  ACADEMIC: 'academic',
  CAREER_PATHWAY: 'career_pathway',
  // 7. OPERATIONS
  FINANCE: 'finance',
  DOCUMENTS: 'documents',
  ANNOUNCEMENTS: 'announcements',
  SEASONS: 'seasons',
  // 8. PFAP AI ASSISTANT (Phase 0)
  AI_ASSISTANT_VIEW: 'ai_assistant_view',
  AI_ASSISTANT_QUERY: 'ai_assistant_query',
  AI_ASSISTANT_PLAYER_SUMMARY: 'ai_assistant_player_summary',
  AI_ASSISTANT_SQUAD_SUMMARY: 'ai_assistant_squad_summary',
  AI_ASSISTANT_STAFF_ACTIONS: 'ai_assistant_staff_actions',
  AI_ASSISTANT_EXTERNAL_FALLBACK: 'ai_assistant_external_fallback',
  AI_ASSISTANT_ADMIN_SETTINGS: 'ai_assistant_admin_settings',
  AI_ASSISTANT_AUDIT_LOGS: 'ai_assistant_audit_logs',
});

// Full list of valid module keys — used by the CI consistency checker.
export const PERMISSION_MODULE_KEYS = Object.freeze(Object.values(PERMISSION_MODULES));
